<template>
  <b-modal
    id="modal-update-status"
    ref="modal"
    title="Update Invoice Status"
    :ok-title="isRevisedSelected ? 'Continue' : 'Update'"
    cancel-variant="outline-secondary"
    @ok="handleOk"
    @hidden="reset"
  >
    <b-form>
      <b-form-group label="Invoice Number">
        <b-form-input :value="form.invoice_number" readonly />
      </b-form-group>

      <b-form-group label="Status *">
        <b-form-select v-model="form.status" :options="statusOptions" />
      </b-form-group>

      <b-form-group v-if="needsPaidFields" label="Paid Date *">
        <b-form-input v-model="form.paid_date" type="date" />
      </b-form-group>

      <b-form-group v-if="needsPaidFields" :label="paidAmountLabel">
        <b-form-input
          v-model.number="form.paid_amount"
          type="number"
          step="0.01"
          min="0"
          :placeholder="isPaid ? 'Full invoice amount' : 'Enter amount paid so far'"
        />
        <small v-if="form.total_amount" class="text-muted d-block mt-1">
          Invoice total: ₹{{ formatAmount(form.total_amount) }}
          <span v-if="form.paid_amount != null && form.paid_amount !== ''">
            · Balance: ₹{{ formatAmount(Math.max(0, Number(form.total_amount) - Number(form.paid_amount || 0))) }}
          </span>
        </small>
      </b-form-group>

      <b-form-group v-if="needsShortPaymentReason" label="Reason for short payment *">
        <b-form-select
          v-model="form.short_payment_reason"
          :options="shortPaymentReasonOptions"
        />
      </b-form-group>

      <b-form-group v-if="needsShortPaymentReason" label="Comment">
        <b-form-textarea
          v-model="form.comment"
          rows="2"
          placeholder="Optional notes (e.g. deduction details)"
        />
      </b-form-group>

      <b-form-group v-if="form.status === 'Unpaid'" label="Comment">
        <b-form-textarea v-model="form.comment" rows="2" placeholder="Optional payment comment" />
      </b-form-group>

      <b-form-group v-if="form.status === 'Cancelled' || form.status === 'Disputed'" label="Comment">
        <b-form-textarea v-model="form.comment" rows="2" placeholder="Add comment" />
      </b-form-group>

      <!-- Revised: R1 filed + pointer to full revision workflow on Update Invoice page -->
      <template v-if="isRevisedSelected">
        <b-form-group label="Is R1 Filed? *">
          <b-form-select v-model="form.r1_filed" :options="r1FiledOptions" />
        </b-form-group>
        <b-alert variant="info" show class="small py-2 px-2 mb-0">
          Click <strong>Continue</strong> to set status to <strong>{{ REVISED_STATUS }}</strong>, then finish
          document uploads (revised invoice, R1, debit/credit notes) on the Update Invoice page.
        </b-alert>
      </template>
    </b-form>
  </b-modal>
</template>

<script>
import { BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BAlert } from 'bootstrap-vue'
import moment from 'moment'
import {
  SHORT_PAYMENT_REASONS,
  getShortPaymentReasonLabel,
  buildNotesWithShortPaymentReason,
  isShortPaymentAmount
} from '@/utils/short-payment-reasons'
import {
  DEFAULT_INVOICE_STATUS_OPTIONS,
  mergeInvoiceStatusOptions,
  REVISED_STATUS,
  isRevisedStatusValue
} from '@/utils/invoice-status-options'

const PAID_STATUSES = ['Paid', 'Part Paid', 'Partially Paid']

export default {
  name: 'UpdateStatusModal',
  components: { BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BAlert },
  props: {
    statusOptions: {
      type: Array,
      default: () => mergeInvoiceStatusOptions(DEFAULT_INVOICE_STATUS_OPTIONS)
    }
  },
  data() {
    return {
      REVISED_STATUS,
      form: this.emptyForm(),
      shortPaymentReasonOptions: SHORT_PAYMENT_REASONS,
      r1FiledOptions: [
        { text: 'Select...', value: null },
        { text: 'Yes', value: 'Yes' },
        { text: 'No', value: 'No' }
      ]
    }
  },
  computed: {
    isRevisedSelected() {
      return this.isRevisedStatusValue(this.form.status)
    },
    isPaid() {
      return String(this.form.status || '').toLowerCase() === 'paid'
    },
    isPartialPaid() {
      const s = String(this.form.status || '').toLowerCase().replace(/\s+/g, ' ')
      return s === 'part paid' || s === 'partially paid'
    },
    needsPaidFields() {
      return this.isPaid || this.isPartialPaid
    },
    paidAmountLabel() {
      return this.isPaid ? 'Paid Amount * (full invoice)' : 'Paid Amount *'
    },
    /** Part Paid always; Paid only when amount is short of invoice total. */
    needsShortPaymentReason() {
      if (this.isPartialPaid) return true
      if (this.isPaid) return isShortPaymentAmount(this.form.paid_amount, this.form.total_amount)
      return false
    },
    shortPaymentReasonLabel() {
      return getShortPaymentReasonLabel(this.form.short_payment_reason)
    }
  },
  watch: {
    'form.status'(status) {
      if (!status) return
      const key = String(status).toLowerCase().replace(/\s+/g, ' ')
      if (key === 'paid') {
        if (this.form.paid_amount == null || this.form.paid_amount === '' || Number(this.form.paid_amount) === 0) {
          this.form.paid_amount = Number(this.form.total_amount) || 0
        }
        if (!this.form.paid_date) {
          this.form.paid_date = moment().format('YYYY-MM-DD')
        }
        // Canonical API value
        if (status !== 'Paid') this.form.status = 'Paid'
      } else if (key === 'partially paid') {
        this.form.status = 'Part Paid'
        if (!this.form.paid_date) {
          this.form.paid_date = moment().format('YYYY-MM-DD')
        }
      } else if (key === 'part paid') {
        if (!this.form.paid_date) {
          this.form.paid_date = moment().format('YYYY-MM-DD')
        }
      }
      if (!this.needsShortPaymentReason) {
        this.form.short_payment_reason = null
      }
    },
    needsShortPaymentReason(val) {
      if (!val) this.form.short_payment_reason = null
    }
  },
  methods: {
    isRevisedStatusValue,
    emptyForm() {
      return {
        invoice_id: null,
        invoice_number: '',
        status: null,
        paid_date: null,
        paid_amount: null,
        total_amount: 0,
        previous_paid_amount: 0,
        comment: '',
        short_payment_reason: null,
        r1_filed: null
      }
    },
    formatAmount(num) {
      if (num === null || num === undefined || isNaN(num)) return '0'
      return parseFloat(num).toLocaleString('en-IN', { maximumFractionDigits: 2 })
    },
    show(invoice) {
      const total = parseFloat(invoice.total_amount) || parseFloat(invoice.amount_with_gst) || 0
      const paid = parseFloat(invoice.paid_amount) || 0
      let status = invoice.status || null
      const statusKey = String(status || '').toLowerCase().replace(/\s+/g, ' ')
      if (statusKey === 'partially paid') status = 'Part Paid'

      this.form = {
        invoice_id: invoice.invoice_id || invoice.id,
        invoice_number: invoice.invoice_id || invoice.invoice_number || '',
        status,
        paid_date: invoice.paid_date || (PAID_STATUSES.includes(status) ? moment().format('YYYY-MM-DD') : null),
        paid_amount: statusKey === 'paid' && !paid ? total : (paid || null),
        total_amount: total,
        previous_paid_amount: paid,
        comment: invoice.payment_comment || invoice.comment || '',
        short_payment_reason: invoice.short_payment_reason || invoice.payment_hold_reason || null,
        r1_filed: invoice.r1_filed || null
      }
      this.$nextTick(() => this.$refs.modal && this.$refs.modal.show())
    },
    hide() {
      this.$refs.modal && this.$refs.modal.hide()
    },
    handleOk(bvEvt) {
      bvEvt.preventDefault()
      if (!this.form.status) {
        this.$bvToast.toast('Status is required.', { title: 'Validation', variant: 'warning', solid: true })
        return
      }

      if (this.isRevisedSelected) {
        if (!this.form.r1_filed) {
          this.$bvToast.toast('R1 Filed status is required for Revised invoices.', { title: 'Validation', variant: 'warning', solid: true })
          return
        }
        this.form.status = this.REVISED_STATUS
        this.$emit('open-revise', {
          invoice_id: this.form.invoice_id,
          r1_filed: this.form.r1_filed
        })
        return
      }

      if (this.needsPaidFields) {
        if (!this.form.paid_date) {
          this.$bvToast.toast('Paid Date is required for Paid / Part Paid.', { title: 'Validation', variant: 'warning', solid: true })
          return
        }
        const amount = Number(this.form.paid_amount)
        if (this.form.paid_amount === null || this.form.paid_amount === '' || isNaN(amount) || amount < 0) {
          this.$bvToast.toast('Paid Amount is required for Paid / Part Paid.', { title: 'Validation', variant: 'warning', solid: true })
          return
        }
        if (this.isPartialPaid && amount <= 0) {
          this.$bvToast.toast('Part Paid amount must be greater than 0.', { title: 'Validation', variant: 'warning', solid: true })
          return
        }
        if (this.isPartialPaid && this.form.total_amount > 0 && amount >= this.form.total_amount) {
          this.$bvToast.toast('Amount covers the full invoice — use Paid, or enter a lower Part Paid amount.', {
            title: 'Validation',
            variant: 'warning',
            solid: true
          })
          return
        }
      }

      if (this.needsShortPaymentReason && !this.form.short_payment_reason) {
        this.$bvToast.toast('Please select a reason for short payment.', { title: 'Validation', variant: 'warning', solid: true })
        return
      }

      const reasonCode = this.needsShortPaymentReason ? this.form.short_payment_reason : null
      const reasonLabel = this.needsShortPaymentReason ? this.shortPaymentReasonLabel : ''
      const comment = this.needsShortPaymentReason
        ? buildNotesWithShortPaymentReason(this.form.comment, reasonLabel)
        : (this.form.comment || '')

      const payload = {
        invoice_id: this.form.invoice_id,
        status: this.isPartialPaid ? 'Part Paid' : this.form.status,
        comment,
        short_payment_reason: reasonCode,
        short_payment_reason_label: reasonLabel || null,
        payment_hold_reason: reasonCode,
        hold_reason: reasonLabel || null
      }

      if (this.needsPaidFields) {
        // Keep entered paid amount (including short full payment under Paid) — do not force to total
        const paidAmount = Number(this.form.paid_amount)
        payload.paid_date = this.form.paid_date
        payload.paid_amount = Number(paidAmount.toFixed(2))
      }

      this.$emit('save', payload)
    },
    reset() {
      this.form = this.emptyForm()
    }
  }
}
</script>
